import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ShoppingBag, TrendingUp, Plus } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalProducts: 0, totalOrders: 0, recentOrders: [] });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [productsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/products`), axios.get(`${API}/admin/orders`)
        ]);
        setStats({
          totalProducts: productsRes.data.length,
          totalOrders: ordersRes.data.length,
          recentOrders: ordersRes.data.slice(0, 5)
        });
      } catch (error) { console.error('Error fetching stats:', error); }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: 'Total Products', value: stats.totalProducts, icon: Package, accent: '#C4A265' },
    { label: 'Total Orders', value: stats.totalOrders, icon: ShoppingBag, accent: '#3E2723' },
    { label: 'Recent Activity', value: stats.recentOrders.length, icon: TrendingUp, accent: '#C4A265' }
  ];

  return (
    <AdminLayout>
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif text-[#3E2723] mb-6 sm:mb-8">Dashboard</h1>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {statCards.map((card, i) => (
            <div key={i} className="bg-[#FEFCF9] border border-[#E8DCC8] p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#7A6B5D] mb-1 tracking-wider uppercase">{card.label}</p>
                  <p className="text-3xl font-bold text-[#3E2723]">{card.value}</p>
                </div>
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: card.accent + '15' }}>
                  <card.icon className="w-5 h-5" style={{ color: card.accent }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-4 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-base sm:text-lg font-serif text-[#3E2723] mb-3 sm:mb-4">Quick Actions</h2>
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
            <button onClick={() => navigate('/admin/products/add')} className="flex items-center space-x-2 bg-[#3E2723] text-[#FEFCF9] px-5 py-2.5 font-medium hover:bg-[#C4A265] transition-colors text-sm tracking-wider uppercase">
              <Plus className="w-4 h-4" /><span>Add Product</span>
            </button>
            <button onClick={() => navigate('/admin/products')} className="flex items-center space-x-2 border border-[#3E2723] text-[#3E2723] px-5 py-2.5 font-medium hover:bg-[#F5EFE6] transition-colors text-sm tracking-wider uppercase">
              <Package className="w-4 h-4" /><span>Products</span>
            </button>
            <button onClick={() => navigate('/admin/orders')} className="flex items-center space-x-2 border border-[#C4A265] text-[#C4A265] px-5 py-2.5 font-medium hover:bg-[#F5EFE6] transition-colors text-sm tracking-wider uppercase">
              <ShoppingBag className="w-4 h-4" /><span>Orders</span>
            </button>
          </div>
        </div>

        <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-serif text-[#3E2723] mb-3 sm:mb-4">Recent Orders</h2>
          {stats.recentOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E8DCC8]">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#7A6B5D] tracking-wider uppercase">Order #</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#7A6B5D] tracking-wider uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#7A6B5D] tracking-wider uppercase">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#7A6B5D] tracking-wider uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentOrders.map((order) => (
                    <tr key={order.id} className="border-b border-[#F5EFE6] hover:bg-[#F5EFE6]/50">
                      <td className="py-3 px-4 text-sm text-[#3E2723]">{order.order_number}</td>
                      <td className="py-3 px-4 text-sm text-[#7A6B5D]">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-sm font-medium text-[#3E2723]">₹{order.total_amount.toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2.5 py-1 text-xs font-medium ${
                          order.status === 'paid' ? 'bg-green-50 text-green-700' :
                          order.status === 'shipped' ? 'bg-blue-50 text-blue-700' :
                          'bg-yellow-50 text-yellow-700'
                        }`}>{order.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[#7A6B5D] text-center py-8 text-sm">No orders yet</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
